import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';
import { toast, Toaster } from 'sonner';
import { Receipt, FileText, Download, Building2, User, CheckCircle2, ArrowLeft, Loader2, Mail } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BASE}/api` });
const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

// Pagina pubblica aperta dal QR dello scontrino: due opzioni (recibo / factura).
export default function ReceiptLanding() {
  const { orderId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('options'); // 'options' | 'factura' | 'done'
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    tipo: 'particular', nombre: '', nif: '', direccion: '', cp: '',
    poblacion: '', provincia: '', pais: 'España', email: '', send_email: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await pub.get(`/receipts/info/${orderId}`);
        setInfo(data);
        if (data.receptor) setForm((f) => ({ ...f, ...data.receptor }));
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudo cargar el pedido');
      } finally { setLoading(false); }
    })();
  }, [orderId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openRecibo = () => window.open(`${BASE}/api/receipts/pdf/${orderId}`, '_blank');

  const submitFactura = async () => {
    if (!form.nombre.trim()) { toast.error('Introduce el nombre o razón social'); return; }
    if (!form.nif.trim()) { toast.error('Introduce el NIF / CIF'); return; }
    setSubmitting(true);
    try {
      const { data } = await pub.post(`/receipts/factura/${orderId}`, form);
      // Download del PDF dal base64
      const bytes = atob(data.pdf_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = data.filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      setDone({ numero: data.numero, emailed: data.emailed });
      setView('done');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar la factura');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <Card className="p-8 text-center max-w-sm" data-testid="recibo-error">
          <p className="text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  const field = (key, label, opts = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`f-${key}`}>{label}{opts.required && <span className="text-destructive"> *</span>}</Label>
      <Input id={`f-${key}`} type={opts.type || 'text'} value={form[key]}
        onChange={(e) => set(key, e.target.value)} placeholder={opts.placeholder || ''}
        className="h-11" data-testid={`factura-${key}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <Toaster position="top-center" richColors />
      <div className="mx-auto max-w-lg">
        {/* Intestazione attività */}
        <div className="text-center mb-6">
          <h1 className="font-display text-2xl font-semibold" data-testid="recibo-business">{info.restaurant?.name}</h1>
          {info.restaurant?.address && (
            <p className="text-sm text-muted-foreground whitespace-pre-line">{info.restaurant.address}</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            {info.table_name ? `${info.table_name} · ` : ''}{info.date} · <span className="font-semibold text-foreground">{eur(info.total)}</span>
          </p>
        </div>

        {view === 'options' && (
          <div className="space-y-4" data-testid="recibo-options">
            <button onClick={openRecibo} data-testid="option-recibo"
              className="w-full text-left rounded-2xl border border-border bg-card p-5 flex items-center gap-4 transition-colors hover:border-primary hover:bg-accent">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Receipt className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-base">Ver recibo</div>
                <div className="text-sm text-muted-foreground">Consulta y descarga el recibo de tu compra</div>
              </div>
              <Download className="h-5 w-5 text-muted-foreground" />
            </button>

            <button onClick={() => setView('factura')} data-testid="option-factura"
              className="w-full text-left rounded-2xl border border-border bg-card p-5 flex items-center gap-4 transition-colors hover:border-primary hover:bg-accent">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-base">Solicitar factura</div>
                <div className="text-sm text-muted-foreground">Genera tu factura personalizada (particular o empresa)</div>
              </div>
              <ArrowLeft className="h-5 w-5 text-muted-foreground rotate-180" />
            </button>
          </div>
        )}

        {view === 'factura' && (
          <Card className="p-5" data-testid="factura-form">
            <button onClick={() => setView('options')} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="factura-back">
              <ArrowLeft className="h-4 w-4" /> Volver
            </button>
            <h2 className="font-display text-xl font-semibold mb-1">Datos de facturación</h2>
            <p className="text-sm text-muted-foreground mb-5">Rellena tus datos para generar la factura.</p>

            {/* Tipo: particular / empresa */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[['particular', 'Particular', User], ['empresa', 'Empresa', Building2]].map(([val, lbl, Icon]) => (
                <button key={val} onClick={() => set('tipo', val)} data-testid={`factura-tipo-${val}`}
                  className={cn('h-12 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium transition-colors',
                    form.tipo === val ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted')}>
                  <Icon className="h-4 w-4" /> {lbl}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {field('nombre', form.tipo === 'empresa' ? 'Razón social' : 'Nombre y apellidos', { required: true, placeholder: form.tipo === 'empresa' ? 'Mi Empresa S.L.' : 'Nombre Apellidos' })}
              {field('nif', form.tipo === 'empresa' ? 'CIF' : 'NIF / DNI', { required: true, placeholder: form.tipo === 'empresa' ? 'B12345678' : '12345678Z' })}
              {field('direccion', 'Dirección', { placeholder: 'Calle y número' })}
              <div className="grid grid-cols-3 gap-3">
                {field('cp', 'Código postal', { placeholder: '03001' })}
                <div className="col-span-2">{field('poblacion', 'Población')}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field('provincia', 'Provincia')}
                {field('pais', 'País')}
              </div>
              {field('email', 'Email (para recibir la factura)', { type: 'email', placeholder: 'tucorreo@ejemplo.com' })}

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox checked={form.send_email} onCheckedChange={(v) => set('send_email', !!v)} data-testid="factura-send-email" />
                <span className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="h-4 w-4" /> Enviarme la factura por email</span>
              </label>

              <Button onClick={submitFactura} disabled={submitting} className="w-full h-12 rounded-xl" data-testid="factura-submit">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Download className="h-5 w-5 mr-2" /> Generar y descargar factura</>}
              </Button>
            </div>
          </Card>
        )}

        {view === 'done' && (
          <Card className="p-8 text-center" data-testid="factura-done">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-9 w-9 text-primary" />
            </div>
            <h2 className="font-display text-xl font-semibold mb-1">¡Factura generada!</h2>
            <p className="text-sm text-muted-foreground">Nº <span className="font-semibold text-foreground">{done?.numero}</span></p>
            <p className="text-sm text-muted-foreground mt-1">
              {done?.emailed ? 'Te la hemos enviado por email y descargado en tu dispositivo.' : 'La factura se ha descargado en tu dispositivo.'}
            </p>
            <Button variant="outline" className="mt-6 rounded-xl" onClick={() => setView('options')} data-testid="factura-done-back">
              Volver
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
